import {
  CommuteSettingsValidationError,
  serializeCommuteSettingsDraft,
  WorkingTemplateCommuteSettingsDraft
} from "../../../../components/w_s_tabs/recording_time/commute-settings.model";
import { WorkingTemplateCommuteSettings } from "../../../../api/working-template-commute-settings-api.types";
import { WorkingTemplateSelectedSubTab } from "./working-template-save-orchestrator";

export { CommuteSettingsValidationError };

export type WorkingTemplateCommuteSaveStage =
  | "idle"
  | "basic-saving"
  | "commute-saving"
  | "partial-failure"
  | "success";

export interface WorkingTemplateCommuteSaveInput {
  selectedSubTab: WorkingTemplateSelectedSubTab;
  workingTemplateIdx?: number;
  draft: WorkingTemplateCommuteSettingsDraft;
  basicFingerprint: string;
  savedBasicFingerprint: string | null;
  createBasic: () => Promise<number>;
  updateBasic: (workingTemplateIdx: number) => Promise<void>;
  putCommute: (
    workingTemplateIdx: number,
    draft: WorkingTemplateCommuteSettingsDraft
  ) => Promise<WorkingTemplateCommuteSettings>;
  onStage: (stage: WorkingTemplateCommuteSaveStage) => void;
  onBasicSaved: (workingTemplateIdx: number, fingerprint: string) => void;
  onCommuteSaved: (settings: WorkingTemplateCommuteSettings) => void;
  onCommuteFailed: (error: unknown) => void;
}

export type WorkingTemplateCommuteSaveResult =
  | {
      status: "success";
      workingTemplateIdx: number;
      settings: WorkingTemplateCommuteSettings;
    }
  | {
      status: "partial-failure";
      workingTemplateIdx: number;
      draft: WorkingTemplateCommuteSettingsDraft;
      error: unknown;
    };

export function shouldPersistCommuteSettings(
  selectedSubTab: WorkingTemplateSelectedSubTab
): boolean {
  return selectedSubTab === "RECORDING_TIME";
}

export async function saveWorkingTemplateCommuteSettings(
  input: WorkingTemplateCommuteSaveInput
): Promise<WorkingTemplateCommuteSaveResult> {
  if (!shouldPersistCommuteSettings(input.selectedSubTab)) {
    throw new CommuteSettingsValidationError({});
  }

  const draft = serializeCommuteSettingsDraft(input.draft);
  let workingTemplateIdx = input.workingTemplateIdx;
  const basicIsCurrent =
    Boolean(workingTemplateIdx) &&
    input.savedBasicFingerprint === input.basicFingerprint;

  if (!basicIsCurrent) {
    input.onStage("basic-saving");
    if (workingTemplateIdx) {
      await input.updateBasic(workingTemplateIdx);
    } else {
      workingTemplateIdx = await input.createBasic();
    }
    input.onBasicSaved(workingTemplateIdx, input.basicFingerprint);
  }

  input.onStage("commute-saving");
  try {
    const settings = await input.putCommute(workingTemplateIdx as number, draft);
    input.onCommuteSaved(settings);
    input.onStage("success");
    return {
      status: "success",
      workingTemplateIdx: workingTemplateIdx as number,
      settings
    };
  } catch (error) {
    input.onCommuteFailed(error);
    input.onStage("partial-failure");
    return {
      status: "partial-failure",
      workingTemplateIdx: workingTemplateIdx as number,
      draft,
      error
    };
  }
}

export interface WorkingTemplateCommuteSaveCoordinator {
  run(
    input: WorkingTemplateCommuteSaveInput
  ): Promise<WorkingTemplateCommuteSaveResult>;
  isRunning(): boolean;
}

export function createWorkingTemplateCommuteSaveCoordinator(): WorkingTemplateCommuteSaveCoordinator {
  let active: Promise<WorkingTemplateCommuteSaveResult> | null = null;
  return {
    run(input) {
      if (active) return active;
      active = saveWorkingTemplateCommuteSettings(input).finally(() => {
        active = null;
      });
      return active;
    },
    isRunning() {
      return active !== null;
    }
  };
}
